import { useEffect, useState } from 'react';
import { useClubContext } from './useClubContext';
import type { Business, Event, Artist } from '@/features/clubs/types/types';

interface UseClubDataReturn {
  club: Business | null;
  events: Event[] | null;
  artists: Artist[] | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to get club data with caching
 * @param handle - The club handle
 * @param autoFetch - Whether to automatically fetch data if not cached (default: true)
 * @returns Club data, events, artists, loading state, error state, and refetch function
 */
export const useClubData = (handle: string, autoFetch: boolean = true): UseClubDataReturn => {
  const {
    getClubData,
    getClubEvents,
    getClubArtists,
    fetchClubData,
    fetchClubEvents,
    fetchClubArtists,
    isLoading,
    getError,
    setCurrentClub,
    invalidateOtherClubs,
    clearCurrentClub
  } = useClubContext();
  const [localLoading, setLocalLoading] = useState(false);

  // Get cached data
  const club = getClubData(handle);
  const events = getClubEvents(handle);
  const artists = getClubArtists(handle);
  const contextLoading = isLoading(handle);
  const error = getError(handle);

  // Handle club switching and cache invalidation
  useEffect(() => {
    if (handle) {
      // Set current club and invalidate others if switching
      setCurrentClub(handle);
      invalidateOtherClubs(handle);
    }

    // Cleanup when component unmounts or handle changes
    return () => {
      if (handle) {
        // Clear current club when navigating away
        clearCurrentClub();
      }
    };
  }, [handle, setCurrentClub, invalidateOtherClubs, clearCurrentClub]);

  // Determine if we need to fetch data
  // Only auto-fetch if we have a handle, autoFetch is on, we don't have data,
  // we aren't already loading from context, and there's no error
  const shouldFetch = !!(handle && autoFetch && !club && !contextLoading && !error);

  // Auto-fetch if data is not available and autoFetch is true
  useEffect(() => {
    if (shouldFetch) {
      setLocalLoading(true);
      // Fetch club data, events, and artists in parallel
      Promise.all([
        fetchClubData(handle),
        fetchClubEvents(handle),
        fetchClubArtists(handle)
      ]).finally(() => {
        setLocalLoading(false);
      });
    }
  }, [shouldFetch, handle, fetchClubData, fetchClubEvents, fetchClubArtists]);

  const refetch = async () => {
    if (handle) {
      setLocalLoading(true);
      await Promise.all([
        fetchClubData(handle),
        fetchClubEvents(handle),
        fetchClubArtists(handle)
      ]);
      setLocalLoading(false);
    }
  };

  return {
    club,
    events,
    artists,
    loading: contextLoading || localLoading || shouldFetch,
    error,
    refetch,
  };
};

